import { type ComponentPropsWithRef, useImperativeHandle, useRef } from "react";
import type { ResponsiveImage as ResponsiveImageType } from "@/types";
import { cn } from "@/utils/style.ts";

export type ResponsiveImageProps = Omit<ComponentPropsWithRef<"img">, "src" | "srcSet"> & {
    image: ResponsiveImageType;
};
export default function ResponsiveImage({ image, alt, className, ...props }: ResponsiveImageProps) {
    const ref = useRef<HTMLImageElement>(null);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(props.ref, () => ref.current!);

    const smallestImageSize = Object.keys(image.responsivePaths).toSorted((a, b) => parseFloat(a) - parseFloat(b))[0];

    const imageSrc = image.responsivePaths[smallestImageSize] ?? null;
    const imageAlt = image.assetView.altText ?? alt ?? "";

    const imageSrcSet = Object.entries(image.responsivePaths)
        .map<string>(([viewport, url]) => `${url} ${viewport}`)
        .join(", ");

    if (!imageSrc) return null;

    return (
        <img
            {...props}
            className={cn("responsive-image", className)}
            ref={ref}
            src={imageSrc}
            alt={imageAlt}
            srcSet={imageSrcSet}
            sizes={props.sizes ?? "100%"}
        />
    );
}
